import Typography from "antd/lib/typography";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { formatErrorMessage } from "src/utils/formatErrorMessage";

import { Feature, useCheckFeatureFlag } from "@sol/features";
import { useRouter } from "@sol/routing";
import {
    getResourceIRI,
    OrderingDirection,
    useBrandListQuery,
    useCurrentBrandQuery,
    useCurrentBrandUpdateMutation,
} from "@sol/sdk";
import { Select } from "@sol/uikit/data-entry/Select/Select";
import { message } from "@sol/uikit/feedback/Message/Message";

const ALL_BRANDS_PER_PAGE = 1000;

export const BrandSelect = () => {
    const [t] = useTranslation();
    const { reload } = useRouter();
    const canChange = useCheckFeatureFlag()(Feature.CURRENT_BRAND_WRITE);
    const [messageApi, contextHolder] = message.useMessage();

    const notifyFailure = (error: unknown) =>
        messageApi.error(formatErrorMessage(error, t("admin.global.state.loadingError")));

    const brands = useBrandListQuery(
        {
            pagination: { page: 1, perPage: ALL_BRANDS_PER_PAGE },
            order: { by: "title", direction: OrderingDirection.ASC },
        },
        { enabled: canChange, onError: notifyFailure },
    );
    const currentBrand = useCurrentBrandQuery({}, { onError: notifyFailure });
    // Only a reload clears an error boundary already tripped by the previous brand
    const scope = useCurrentBrandUpdateMutation({ onSuccess: () => reload(), onError: notifyFailure });

    const scopedBrand = currentBrand.data?.brand;

    // The scoped brand names itself, so the select never falls back to its raw iri while loading
    const brandOptions = [
        ...(scopedBrand ? [{ value: getResourceIRI(scopedBrand), label: scopedBrand.title }] : []),
        ...(brands.data?.["hydra:member"] ?? [])
            .filter(brand => getResourceIRI(brand) !== (scopedBrand && getResourceIRI(scopedBrand)))
            .map(brand => ({ value: getResourceIRI(brand), label: brand.title })),
    ];

    // Read only for whoever cannot change it: the scope still says what they are looking at
    if (!canChange) {
        return (
            <Typography.Text data-cy="brand-scope" className="font-ant-strong text-color-ant-primary">
                {scopedBrand?.title ?? t("admin.currentBrand.show.noBrand")}
            </Typography.Text>
        );
    }

    return (
        <div className={clsx("flex flex-col gap-ant-xs", scopedBrand ? "w-48 sm:w-64" : "w-fit")}>
            {contextHolder}

            <Select
                data-cy="brand-select"
                aria-label={t("component.BrandSelect.label")}
                prefix={
                    <span className="font-ant-strong text-color-ant-primary">{t("component.BrandSelect.label")}</span>
                }
                placeholder={t("component.BrandSelect.allBrands")}
                showSearch
                optionFilterProp="label"
                popupMatchSelectWidth={false}
                options={brandOptions}
                value={scopedBrand && getResourceIRI(scopedBrand)}
                loading={brands.isLoading || currentBrand.isLoading || scope.isPending}
                allowClear
                onChange={brand => scope.mutate({ brand })}
            />
        </div>
    );
};
